import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle } from "lucide-react";

const CATEGORIES = ["DeFi", "L1", "L2", "GameFi", "NFT", "AI", "Stablecoin", "DEX", "Lending", "Other"];

export default function ProjectApply() {
  const [form, setForm] = useState({
    name: "", ticker: "", website: "", whitepaper: "",
    category: "", launch_date: "", description: "",
    twitter: "", telegram: "", discord: "", contact_email: "", logo_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.website || !form.category || !form.description || !form.contact_email) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/projects/apply", form);
      setSuccess(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="pt-28 pb-20 max-w-2xl mx-auto px-6 md:px-10 text-center">
      <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
      <h1 className="font-display font-bold text-4xl text-[var(--text-primary)] mb-4">Application Submitted!</h1>
      <p className="text-[var(--text-secondary)] mb-8">
        We've received your project application and will review it within 48 hours. You'll hear from us at {form.contact_email}.
      </p>
      <Link to="/projects" className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-3 hover:bg-yellow-500 transition inline-block">
        View Projects →
      </Link>
    </div>
  );



  return (
    <div className="pt-10 pb-20 max-w-2xl mx-auto px-6 md:px-10">
      <Link to="/projects" className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] hover:text-yellow-400 transition mb-8 block">
        ← Back to Projects
      </Link>

      <h1 className="font-display font-bold text-5xl text-[var(--text-primary)] tracking-tighter mb-2">
        Submit Your Project
      </h1>
      <p className="text-[var(--text-secondary)] mb-10">
        Apply to be listed on MediaCrypto. Featured listings include a dedicated article, social promotion, and top placement on the projects page.
      </p>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Project Name <span className="text-yellow-400">*</span></label>
            <input placeholder="e.g. HyperChain" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Ticker Symbol</label>
            <input placeholder="e.g. HYP" value={form.ticker} onChange={(e) => set("ticker", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Website <span className="text-yellow-400">*</span></label>
          <input placeholder="https://yourproject.io" value={form.website} onChange={(e) => set("website", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Whitepaper / Docs</label>
          <input placeholder="https://docs.yourproject.io" value={form.whitepaper} onChange={(e) => set("whitepaper", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Logo URL</label>
          <input placeholder="https://yourproject.io/logo.png" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Category <span className="text-yellow-400">*</span></label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 transition">
              <option value="">Select category</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Launch Date</label>
            <input placeholder="e.g. Q3 2026 or 2026-09-01" value={form.launch_date} onChange={(e) => set("launch_date", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Description <span className="text-yellow-400">*</span></label>
          <textarea placeholder="Describe your project in 2-3 sentences..." value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition resize-none" />
        </div>

        <div className="border-t border-[var(--border-soft)] pt-6">
          <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-4">Social Links</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Twitter / X</label>
              <input placeholder="https://x.com/yourproject" value={form.twitter} onChange={(e) => set("twitter", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Telegram</label>
              <input placeholder="https://t.me/yourproject" value={form.telegram} onChange={(e) => set("telegram", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Discord</label>
              <input placeholder="https://discord.gg/yourproject" value={form.discord} onChange={(e) => set("discord", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">Contact Email <span className="text-yellow-400">*</span></label>
          <input type="email" placeholder="founder@yourproject.io" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400 transition" />
        </div>

        {error && <p className="text-red-400 text-sm font-mono">{error}</p>}

        <button onClick={submit} disabled={loading} className="w-full bg-yellow-400 text-black font-bold uppercase tracking-wide py-4 hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? "Submitting..." : "Submit Application →"}
        </button>

        <p className="text-xs text-[var(--text-tertiary)] text-center">
          We review all applications within 48 hours. Featured listings are available for projects that meet our quality standards.
        </p>
      </div>
    </div>
  );
}